// Slice RTK per le entity CRUD (lista flat di oggetti con `id`).
//
// Pattern: ogni slice è un array di oggetti `{ id, ...rest }` con i 4
// reducer canonici `set`, `add`, `update`, `delete`. Manteniamo lo shape
// array (non normalizzato) perché i consumer usano direttamente
// `state.vehicles.map(...)`, `state.vehicles.find(...)`, ecc. — passare a
// `createEntityAdapter` (shape `{ ids, entities }`) richiederebbe l'audit
// e la riscrittura di decine di consumer.
//
// Per le slice future che possono permettersi lo shape normalized,
// usare `createEntityAdapter` di RTK direttamente (vedi docs RTK).

import { createSlice } from '@reduxjs/toolkit';

/**
 * Crea una slice CRUD su array di entity.
 * - `set(arr)`: rimpiazza l'intera lista.
 * - `add(entity)`: append.
 * - `update(entity)`: replace by `id`.
 * - `delete(id)`: remove by `id`.
 */
function makeCrudListSlice(name) {
  const slice = createSlice({
    name,
    initialState: [],
    reducers: {
      set: (_state, action) => action.payload ?? [],
      add: (state, action) => {
        state.push(action.payload);
      },
      update: (state, action) => {
        const idx = state.findIndex((e) => e.id === action.payload.id);
        if (idx !== -1) state[idx] = action.payload;
      },
      delete: (state, action) => state.filter((e) => e.id !== action.payload),
    },
  });
  return {
    reducer: slice.reducer,
    set: slice.actions.set,
    add: slice.actions.add,
    update: slice.actions.update,
    delete: slice.actions.delete,
  };
}

const vehicles = makeCrudListSlice('vehicles');
const hubs = makeCrudListSlice('hubs');
const companies = makeCrudListSlice('companies');
const managers = makeCrudListSlice('managers');
const drivers = makeCrudListSlice('drivers');
const maintenance = makeCrudListSlice('maintenance');
const damages = makeCrudListSlice('damages');
const inspections = makeCrudListSlice('inspections');
const processes = makeCrudListSlice('processes');
const refuelings = makeCrudListSlice('refuelings');
const violations = makeCrudListSlice('violations');
// `gestori` e `mezzi` sono array piatti usati solo via `set` (no add/update/
// delete dispatched). Includere comunque qui per coerenza del pattern.
const gestori = makeCrudListSlice('gestori');
const mezzi = makeCrudListSlice('mezzi');

// --- Reducers ---------------------------------------------------------------
export const crudListReducers = {
  vehicles: vehicles.reducer,
  hubs: hubs.reducer,
  companies: companies.reducer,
  managers: managers.reducer,
  drivers: drivers.reducer,
  maintenance: maintenance.reducer,
  damages: damages.reducer,
  inspections: inspections.reducer,
  processes: processes.reducer,
  refuelings: refuelings.reducer,
  violations: violations.reducer,
  gestori: gestori.reducer,
  mezzi: mezzi.reducer,
};

// --- Setters (nomi back-compat) ---------------------------------------------
export const setVehicles = vehicles.set;
export const addVehicle = vehicles.add;
export const updateVehicle = vehicles.update;
export const deleteVehicle = vehicles.delete;

export const setHubs = hubs.set;
export const addHub = hubs.add;
export const updateHub = hubs.update;
export const deleteHub = hubs.delete;

export const setCompanies = companies.set;
export const addCompany = companies.add;
export const updateCompany = companies.update;
export const deleteCompany = companies.delete;

export const setManagers = managers.set;
export const addManager = managers.add;
export const updateManager = managers.update;
export const deleteManager = managers.delete;

export const setDrivers = drivers.set;
export const addDriver = drivers.add;
export const updateDriver = drivers.update;
export const deleteDriver = drivers.delete;

export const setMaintenance = maintenance.set;
export const addMaintenance = maintenance.add;
export const updateMaintenance = maintenance.update;
export const deleteMaintenance = maintenance.delete;

export const setDamages = damages.set;
export const addDamage = damages.add;
export const updateDamage = damages.update;
export const deleteDamage = damages.delete;

export const setInspections = inspections.set;
export const addInspection = inspections.add;
export const updateInspection = inspections.update;
export const deleteInspection = inspections.delete;

export const setProcesses = processes.set;
export const addProcess = processes.add;
export const updateProcess = processes.update;
export const deleteProcess = processes.delete;

export const setRefuelings = refuelings.set;
export const addRefueling = refuelings.add;
export const updateRefueling = refuelings.update;
export const deleteRefueling = refuelings.delete;

export const setViolations = violations.set;
export const addViolation = violations.add;
export const updateViolation = violations.update;
export const deleteViolation = violations.delete;

export const setGestori = gestori.set;
export const setMezzi = mezzi.set;
